import React, { useState } from 'react';
import { AlertTriangle, Trash2, Ban, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { firmasAPI } from '../api';
import { toast } from '../hooks/use-toast';

const MOTIVOS = [
  { value: 'duplicado', label: 'Documento duplicado' },
  { value: 'ya_firmado', label: 'Ya firmado anteriormente' },
  { value: 'error', label: 'Enviado por error' },
  { value: 'documento_incorrecto', label: 'Documento incorrecto' },
  { value: 'otro', label: 'Otro (lo explico abajo)' },
];

// Cancelar (invalida el enlace y avisa al cliente) o eliminar del historial.
const CancelarFirmaDialog = ({ firma, open, onOpenChange, onDone }) => {
  const [motivo, setMotivo] = useState('duplicado');
  const [detalle, setDetalle] = useState('');
  const [avisar, setAvisar] = useState(true);
  const [idioma, setIdioma] = useState('es');
  const [accion, setAccion] = useState(null);

  if (!firma) return null;
  const yaFirmado = firma.estado === 'firmado';

  const cerrar = () => {
    setDetalle('');
    setMotivo('duplicado');
    setAvisar(true);
    onOpenChange(false);
  };

  const ejecutar = async (tipo) => {
    setAccion(tipo);
    try {
      const res = tipo === 'cancelar'
        ? await firmasAPI.cancelar(firma.id, { motivo, detalle, avisar_cliente: avisar, idioma })
        : await firmasAPI.eliminar(firma.id, { motivo, detalle, avisar_cliente: avisar, idioma });
      const aviso = res.data?.email_enviado;
      toast({
        title: tipo === 'cancelar' ? 'Firma cancelada' : 'Petición eliminada',
        description: avisar
          ? (aviso ? 'Se ha avisado al cliente por email' : 'No se pudo avisar al cliente por email')
          : 'No se ha avisado al cliente',
        variant: avisar && aviso === false ? 'destructive' : 'default',
      });
      window.dispatchEvent(new Event('lexduo:firmas'));
      if (onDone) onDone();
      cerrar();
    } catch (e) {
      toast({
        title: 'No se pudo completar',
        description: e?.response?.data?.detail || 'Error inesperado',
        variant: 'destructive',
      });
    } finally {
      setAccion(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : cerrar())}>
      <DialogContent className="max-w-lg" data-testid="cancelar-firma-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Cancelar o eliminar la petición de firma
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-gray-50 border p-3 text-sm">
            <div className="font-medium text-gray-900">{firma.nombre_documento}</div>
            <div className="text-gray-600">
              {firma.firmante_nombre || 'cliente'}
              {firma.email_enviado_a ? ` · ${firma.email_enviado_a}` : ' · sin email'}
              {` · estado: ${firma.estado}`}
            </div>
          </div>

          {yaFirmado && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700"
                 data-testid="cancelar-firma-firmado">
              Este documento ya está firmado: no se puede cancelar ni eliminar.
            </div>
          )}

          <div>
            <Label>Motivo</Label>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              data-testid="cancelar-firma-motivo"
            >
              {MOTIVOS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label>Explicación para el cliente (opcional)</Label>
            <Textarea
              className="mt-1"
              rows={2}
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Se enviará dentro del email como motivo"
              data-testid="cancelar-firma-detalle"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="avisar-cliente-firma" checked={avisar}
                      onCheckedChange={(v) => setAvisar(!!v)}
                      data-testid="cancelar-firma-avisar" />
            <Label htmlFor="avisar-cliente-firma" className="cursor-pointer">
              Avisar al cliente por email de que se cancela la firma
            </Label>
          </div>

          {avisar && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Idioma del email:</span>
              <Button size="sm" variant={idioma === 'es' ? 'default' : 'outline'} className="h-7"
                      onClick={() => setIdioma('es')} data-testid="cancelar-firma-idioma-es">Castellà</Button>
              <Button size="sm" variant={idioma === 'ca' ? 'default' : 'outline'} className="h-7"
                      onClick={() => setIdioma('ca')} data-testid="cancelar-firma-idioma-ca">Català</Button>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={cerrar} data-testid="cancelar-firma-cerrar">Cerrar</Button>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={yaFirmado || !!accion}
                    onClick={() => ejecutar('cancelar')} data-testid="cancelar-firma-confirmar">
              {accion === 'cancelar'
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Ban className="w-4 h-4 mr-1" />}
              Cancelar la firma
            </Button>
            <Button variant="destructive" disabled={yaFirmado || !!accion}
                    onClick={() => ejecutar('eliminar')} data-testid="eliminar-firma-confirmar">
              {accion === 'eliminar'
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Trash2 className="w-4 h-4 mr-1" />}
              Eliminar petición
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelarFirmaDialog;
